/**
 * @name useAuthIntegration
 * @description Hook para autenticação transparente via integração externa
 * 
 * CHANGELOG: 
 * - Modificado createSupabaseSession para usar supabase.auth.setSession() em vez de mock
 * - Agora utiliza o access_token válido do Supabase retornado pela API de integração
 * - Isso resolve o problema de logout automático causado pelo UserContext
 * 
 * @author Gabriel M. Guimarães | gabrielmg7
 * @package src/hooks/useAuthIntegration.ts
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../../../supabaseConfig';
import { useLoadingContext } from '../../../../contexts/LoadingContext/useLoadingContext';
import { useUserContext } from '../../../../contexts/UserContext';
import { useAlert } from '../../../../hooks/useAlert';
import { authIntegrationService } from '../../../../services/auth/AuthIntegrationService';

interface UseAuthIntegrationReturn {
     authenticateFromExternalSystem: (anoLetivo: string, matricula: string) => Promise<boolean>;
     isProcessingIntegration: boolean;
     integrationError: string | null;
     errorType: 'RESPONSAVEL_SEM_EMAIL' | 'DATABASE_ERROR' | 'GENERIC_ERROR' | null; // Tipo específico do erro
     responsavelData: any | null; // Dados do responsável quando não há email
     clearIntegrationError: () => void;
     resetProcessingState: () => void;
}

/**
 * Hook para autenticação transparente
 * 
 * Funcionalidades:
 * - Autentica usuário via dados do sistema parceiro (matrícula + ano letivo)
 * - Cria sessão no Supabase automaticamente
 * - Integra com o UserContext existente
 * - Redireciona automaticamente após autenticação
 */
export const useAuthIntegration = (): UseAuthIntegrationReturn => {
     const navigate = useNavigate();
     const { showSuccess } = useAlert(); // Mantém apenas showSuccess para casos de sucesso
     const { setLoadingState } = useLoadingContext();
     const { forceRefreshAuth } = useUserContext();

     const [isProcessingIntegration, setIsProcessingIntegration] = useState(false);
     const [integrationError, setIntegrationError] = useState<string | null>(null);
     const [errorType, setErrorType] = useState<'RESPONSAVEL_SEM_EMAIL' | 'DATABASE_ERROR' | 'GENERIC_ERROR' | null>(null);
     const [responsavelData, setResponsavelData] = useState<any | null>(null);

     // Controle para evitar reprocessamento
     const [hasProcessedParams, setHasProcessedParams] = useState(false);
     const processingRef = useRef(false);
     const sessionCreationRef = useRef(false);
     const apiCallRef = useRef(false); // Previne múltiplas chamadas à API
     const lastProcessedParams = useRef<{ anoLetivo: string; matricula: string } | null>(null);
     const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Timeout para loading infinito

     // Limpa erro de integração
     const clearIntegrationError = useCallback(() => {
          setIntegrationError(null);
          setErrorType(null);
          setResponsavelData(null); // Limpa dados do responsável junto com o erro
     }, []);

     // Limpa registros antigos de chamadas (executa na inicialização)
     useEffect(() => {
          const cleanupOldRecords = () => {
               const now = Date.now();
               Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('auth_recent_call_')) {
                         const timestamp = localStorage.getItem(key);
                         if (timestamp) {
                              const timeDiff = now - parseInt(timestamp, 10);
                              // Remove registros com mais de 5 minutos
                              if (timeDiff > 300000) {
                                   localStorage.removeItem(key);
                              }
                         }
                    }
               });
          };

          cleanupOldRecords();
          
          // IMPORTANTE: Limpa estados órfãos de loading na inicialização
          const cleanupOrphanedLoadingStates = () => {
               const authIntegrationProcessing = localStorage.getItem('auth-integration-processing');
               const authLoadingActive = localStorage.getItem('auth-loading-active');
               
               if (authIntegrationProcessing || authLoadingActive) {
                    console.log('🧹 Limpando estados órfãos de loading na inicialização');
                    localStorage.removeItem('auth-integration-processing');
                    localStorage.removeItem('auth-loading-active');
                    setLoadingState('auth-loading', false);
               }
          };
          
          cleanupOrphanedLoadingStates();
          
          // 🔧 IMPORTANTE: Carrega estados de erro salvos (caso de re-render)
          const loadSavedErrorStates = () => {
               const savedErrorData = localStorage.getItem('integration-error-data');
               if (savedErrorData) {
                    try {
                         const errorData = JSON.parse(savedErrorData);
                         // Verifica se o erro é recente (menos de 2 minutos)
                         const isRecent = Date.now() - errorData.timestamp < 120000;
                         
                         if (isRecent && errorData.type === 'RESPONSAVEL_SEM_EMAIL') {
                              console.log('🔄 Carregando estado de erro salvo:', errorData);
                              setErrorType('RESPONSAVEL_SEM_EMAIL');
                              setIntegrationError(errorData.message);
                              setResponsavelData(errorData.responsavel);
                              setIsProcessingIntegration(false);
                         } else {
                              // Remove dados antigos
                              localStorage.removeItem('integration-error-data');
                         }
                    } catch (error) {
                         console.warn('Erro ao carregar dados de erro salvos:', error);
                         localStorage.removeItem('integration-error-data');
                    }
               }
          };
          
          loadSavedErrorStates();
     }, [setLoadingState]);

     // Cleanup quando componente for desmontado
     useEffect(() => {
          return () => {
               // Limpa timeout se existir
               if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
               }
               
               // Força limpeza de loading states na desmontagem
               localStorage.removeItem('auth-integration-processing');
               localStorage.removeItem('auth-loading-active');
          };
     }, []);

     // Reseta todos os controles de processamento (para retry)
     const resetProcessingState = useCallback(() => {
          // Limpa timeout de loading se existir
          if (loadingTimeoutRef.current) {
               clearTimeout(loadingTimeoutRef.current);
               loadingTimeoutRef.current = null;
          }
          
          apiCallRef.current = false;
          processingRef.current = false;
          sessionCreationRef.current = false;
          lastProcessedParams.current = null; // Limpa cache de parâmetros
          setHasProcessedParams(false);
          setIsProcessingIntegration(false);
          setResponsavelData(null); // Limpa dados do responsável
          setErrorType(null); // Limpa tipo de erro
          clearIntegrationError();
          
          // Força limpeza de loading states
          setLoadingState('auth-loading', false);
          localStorage.removeItem('auth-integration-processing');
          localStorage.removeItem('auth-loading-active');
          
          // 🔧 IMPORTANTE: Limpa registros de chamadas recentes no localStorage para permitir retry
          Object.keys(localStorage).forEach(key => {
               if (key.startsWith('auth_recent_call_')) {
                    localStorage.removeItem(key);
                    console.log('🧹 Cache de chamada removido:', key);
               }
          });
          
          // 🔧 Limpa dados de erro salvos
          localStorage.removeItem('integration-error-data');
     }, [setLoadingState]);

     /**
      * Busca dados do usuário no banco local usando auth_user_id
      */
     const fetchUserDataFromDatabase = async (authUserId: string) => {
          try {
               const { data, error } = await supabase
                    .from('pessoas')
                    .select('*')
                    .eq('auth_user_id', authUserId)
                    .single();

               if (error || !data) {
                    console.warn('Usuário não encontrado no banco local:', error);
                    return null;
               }

               return data;
          } catch (error) {
               console.error('Erro ao buscar dados do usuário:', error);
               return null;
          }
     };

     /**
      * Define uma sessão válida do Supabase usando o token JWT retornado pela API
      */
     const createSupabaseSession = async (accessToken: string, refreshToken?: string) => {
          try {
               // Evita criação dupla de sessão
               if (sessionCreationRef.current) {
                    console.log('⏸️ Sessão já sendo criada, aguardando...');
                    return;
               }

               sessionCreationRef.current = true;
               console.log('🔐 Criando sessão do Supabase com token de integração...');
               
               // Verificar se já existe uma sessão ativa
               const { data: currentSession } = await supabase.auth.getSession();
               if (currentSession.session && currentSession.session.access_token === accessToken) {
                    console.log('✅ Sessão já existe e é válida, pulando criação');
                    return currentSession.session;
               }
               
               if (!refreshToken) {
                    console.warn('⚠️  API não retornou refresh_token');
               }

               // ESTRATÉGIA 1: Tentar setSession primeiro (método padrão)
               console.log('🔄 Tentativa 1: Usando setSession...');
               const { data, error } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken || accessToken, // Usa access_token como fallback
               });

               if (!error && data.session) {
                    console.log('✅ Sessão criada com setSession');
                    
                    // Busca dados completos no banco local
                    const localUserData = await fetchUserDataFromDatabase(data.session.user.id);
                    
                    if (!localUserData) {
                         throw new Error('Usuário não encontrado no banco de dados local');
                    }

                    console.log('✅ Dados do usuário encontrados no banco local');
                    return data.session;
               }

               // ESTRATÉGIA 2: Se setSession falhou, tentar abordagem alternativa
               console.log('🔄 Tentativa 2: Usando getUser + signInWithPassword...');
               console.error('❌ setSession falhou:', error?.message);

               // Primeiro, valida se o token é válido
               const { data: userData, error: userError } = await supabase.auth.getUser(accessToken);
               
               if (userError || !userData.user) {
                    throw new Error(`Token inválido: ${userError?.message || 'Usuário não encontrado'}`);
               }

               console.log('✅ Token válido, usuário encontrado:', userData.user.email);

               // Busca dados completos no banco local
               const localUserData = await fetchUserDataFromDatabase(userData.user.id);
               
               if (!localUserData) {
                    throw new Error('Usuário não encontrado no banco de dados local');
               }

               // ESTRATÉGIA 3: Tentar fazer login com email/senha se soubermos a senha
               // Isso é uma workaround para criar uma sessão válida
               try {
                    console.log('🔄 Tentativa 3: Fazendo login com credenciais...');
                    
                    // Gera a senha padrão baseada no CPF (mesmo padrão da API)
                    const senhaPadrao = 'IS@' + (localUserData.documento_numero || '123456');
                    
                    const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
                         email: userData.user.email!,
                         password: senhaPadrao
                    });

                    if (!loginError && loginData.session) {
                         console.log('✅ Sessão criada via signInWithPassword');
                         return loginData.session;
                    }

                    console.warn('❌ signInWithPassword falhou:', loginError?.message);
               } catch (loginErr) {
                    console.warn('❌ Erro no login:', loginErr);
               }

               // Se todas as estratégias falharam, lança erro
               throw new Error(`Não foi possível criar sessão válida: ${error?.message || 'Erro desconhecido'}`);

          } catch (error) {
               console.error('❌ Erro ao criar sessão de integração:', error);
               throw error;
          } finally {
               sessionCreationRef.current = false;
          }
     };

     // Controle para evitar reprocessamento

     /**
      * Processa parâmetros da URL para autenticação automática
      * NOTA: Esta lógica só executa se a página não estiver gerenciando a autenticação
      */
     useEffect(() => {
          const processUrlParams = async () => {
               // Evita processamento duplo usando ref
               if (processingRef.current || isProcessingIntegration || hasProcessedParams || apiCallRef.current) {
                    console.log('⏸️ Processamento já em andamento ou já processado, ignorando...');
                    return;
               }

               // IMPORTANTE: Verifica se já há uma sessão ativa antes de processar integração
               const { data: currentSession } = await supabase.auth.getSession();
               if (currentSession?.session) {
                    console.log('⏸️ Sessão já ativa, ignorando processamento de integração automática');
                    return;
               }

               const urlParams = new URLSearchParams(window.location.search);
               const anoLetivo = urlParams.get('ano_letivo');
               const matricula = urlParams.get('matricula');

               // OTIMIZAÇÃO: Só processa se realmente há parâmetros E não foram processados
               if (anoLetivo && matricula && !hasProcessedParams) {
                    console.log('🎯 [useEffect] Parâmetros de integração detectados:', { anoLetivo, matricula });
                    
                    // Marca como processado para evitar reprocessamento
                    setHasProcessedParams(true);
                    
                    // Remove parâmetros da URL imediatamente para evitar reprocessamento
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, '', newUrl);

                    // Usa a função principal para manter consistência
                    await authenticateFromExternalSystem(anoLetivo, matricula);
               }
          };

          // IMPORTANTE: Só executa se não há sessão ativa
          processUrlParams();
     }, []); // Dependências vazias para executar apenas uma vez

     /**
      * Função principal de autenticação via integração
      */
     const authenticateFromExternalSystem = useCallback(async (
          anoLetivo: string, 
          matricula: string
     ): Promise<boolean> => {
          // Verifica se já processou estes mesmos parâmetros
          const currentParams = { anoLetivo, matricula };
          const lastParams = lastProcessedParams.current;
          
          if (lastParams && lastParams.anoLetivo === anoLetivo && lastParams.matricula === matricula) {
               console.log('⏸️ Parâmetros já processados anteriormente, ignorando:', currentParams);
               return false;
          }

          // Verifica se há uma chamada recente no localStorage (para evitar entre recarregamentos)
          const recentCallKey = `auth_recent_call_${anoLetivo}_${matricula}`;
          const recentCall = localStorage.getItem(recentCallKey);
          const now = Date.now();
          
          if (recentCall) {
               const lastCallTime = parseInt(recentCall, 10);
               const timeDiff = now - lastCallTime;
               // Se a última chamada foi há menos de 30 segundos, ignora
               if (timeDiff < 30000) {
                    console.log('⏸️ Chamada recente detectada, ignorando (há', Math.round(timeDiff/1000), 'segundos)');
                    return false;
               }
          }

          // PROTEÇÃO EXTRA PARA NAVEGADORES CHROMIUM
          const userAgent = navigator.userAgent.toLowerCase();
          const isChromiumBased = userAgent.includes('chrome') || userAgent.includes('brave') || userAgent.includes('edg/');
          
          if (isChromiumBased) {
               console.log('🌐 [Integration] Navegador Chromium detectado, aplicando proteções adicionais');
               
               // Verifica se já há uma chamada em andamento específica para Chromium
               const chromiumCallKey = `chromium_auth_call_${anoLetivo}_${matricula}`;
               const chromiumCall = localStorage.getItem(chromiumCallKey);
               
               if (chromiumCall) {
                    const chromiumTimeDiff = now - parseInt(chromiumCall, 10);
                    if (chromiumTimeDiff < 10000) { // 10 segundos para Chromium
                         console.log('⏸️ [Integration] Chamada Chromium recente detectada, ignorando');
                         return false;
                    }
               }
               
               // Registra a chamada Chromium
               localStorage.setItem(chromiumCallKey, now.toString());
               
               // Limpa após 15 segundos
               setTimeout(() => {
                    localStorage.removeItem(chromiumCallKey);
               }, 15000);
          }

          // Previne múltiplas chamadas simultâneas à API
          if (apiCallRef.current || processingRef.current || isProcessingIntegration) {
               console.log('⏸️ Autenticação já em andamento, ignorando nova tentativa...');
               return false;
          }

          // Registra a chamada atual
          localStorage.setItem(recentCallKey, now.toString());
          
          // Armazena os parâmetros que estão sendo processados
          lastProcessedParams.current = currentParams;
          
          apiCallRef.current = true;
          processingRef.current = true;
          setIsProcessingIntegration(true);
          setLoadingState('auth-loading', true);
          
          // Sinaliza que está processando integração para o UserContext
          localStorage.setItem('auth-integration-processing', 'true');
          localStorage.setItem('auth-loading-active', 'true');
          
          // TIMEOUT DE SEGURANÇA: Se não resolver em 30 segundos, força parada
          loadingTimeoutRef.current = setTimeout(() => {
               console.error('🚨 Timeout de segurança: Autenticação travada há mais de 30 segundos');
               setErrorType('GENERIC_ERROR');
               setIntegrationError('Timeout na autenticação. Tente novamente.');
               
               // Força limpeza de todos os estados
               setLoadingState('auth-loading', false);
               setIsProcessingIntegration(false);
               localStorage.removeItem('auth-integration-processing');
               localStorage.removeItem('auth-loading-active');
               apiCallRef.current = false;
               processingRef.current = false;
          }, 30000);
          
          setIntegrationError(null);

          try {
               console.log('🎯 Iniciando autenticação via integração:', { anoLetivo, matricula });
               
               // Chama a API de integração
               const authResult = await authIntegrationService.authenticateViaIntegration({
                    anoLetivo,
                    matricula,
               });

               if (!authResult.success) {
                    console.log('🔍 [Debug] authResult.success = false, authResult:', authResult);
                    
                    // Caso específico: responsável sem email - exibe erro na própria página
                    if (authResult.responsavel) {
                         console.log('⚠️ Responsável sem email detectado, exibindo tela de erro na própria página');
                         console.log('📋 Dados do responsável:', authResult.responsavel);
                         
                         // Limpa timeout de segurança
                         if (loadingTimeoutRef.current) {
                              clearTimeout(loadingTimeoutRef.current);
                              loadingTimeoutRef.current = null;
                         }
                         
                         // Armazena dados do responsável para exibição na tela de erro
                         setResponsavelData(authResult.responsavel);
                         setErrorType('RESPONSAVEL_SEM_EMAIL');
                         setIntegrationError(authResult.error || 'Não é possível criar uma conta pois o responsável financeiro não possui e-mail cadastrado.');
                         
                         // 🔧 Salva dados de erro no localStorage para preservar entre re-renders
                         const errorData = {
                              type: 'RESPONSAVEL_SEM_EMAIL',
                              message: authResult.error || 'Não é possível criar uma conta pois o responsável financeiro não possui e-mail cadastrado.',
                              responsavel: authResult.responsavel,
                              timestamp: Date.now()
                         };
                         localStorage.setItem('integration-error-data', JSON.stringify(errorData));
                         
                         console.log('🎯 [Debug] Estados definidos:', {
                              errorType: 'RESPONSAVEL_SEM_EMAIL',
                              hasResponsavelData: !!authResult.responsavel,
                              error: authResult.error,
                              savedToLocalStorage: true
                         });
                         
                         // IMPORTANTE: Desativar loading e processamento para casos de responsável sem email
                         setLoadingState('auth-loading', false);
                         setIsProcessingIntegration(false); // ⭐ CRÍTICO: Desativa o flag de processamento
                         console.log('🔄 [Debug] setIsProcessingIntegration(false) chamado para responsável sem email');
                         localStorage.removeItem('auth-integration-processing');
                         localStorage.removeItem('auth-loading-active');
                         
                         // 🔧 FORÇA atualização de estado para garantir que middleware veja a mudança
                         setTimeout(() => {
                              setIsProcessingIntegration(false);
                              console.log('🔄 [Debug] Força segunda atualização setIsProcessingIntegration(false)');
                         }, 10);
                         
                         return false;
                    }

                    // Casos específicos de erro de banco de dados
                    const errorMessage = authResult.error || 'Erro na autenticação via integração';
                    
                    if (errorMessage.includes('duplicate key value violates unique constraint "pessoas_email_key"')) {
                         const emailConflictMsg = 'Este e-mail já está cadastrado no sistema. Entre em contato com o suporte para resolver o conflito.';
                         setErrorType('DATABASE_ERROR');
                         setIntegrationError(emailConflictMsg);
                    } else if (errorMessage.includes('Database error saving new user')) {
                         const dbErrorMsg = 'Erro interno ao salvar dados do usuário. Tente novamente em alguns minutos ou entre em contato com o suporte.';
                         setErrorType('DATABASE_ERROR');
                         setIntegrationError(dbErrorMsg);
                    } else if (errorMessage.includes('Database error:')) {
                         const genericDbMsg = 'Erro interno do sistema. Tente novamente em alguns minutos ou entre em contato com o suporte.';
                         setErrorType('DATABASE_ERROR');
                         setIntegrationError(genericDbMsg);
                    } else {
                         // Outros erros
                         setErrorType('GENERIC_ERROR');
                         setIntegrationError(errorMessage);
                    }
                    
                    // Desativar loading para todos os outros casos de erro também
                    // Limpa timeout de segurança
                    if (loadingTimeoutRef.current) {
                         clearTimeout(loadingTimeoutRef.current);
                         loadingTimeoutRef.current = null;
                    }
                    
                    setLoadingState('auth-loading', false);
                    setIsProcessingIntegration(false); // ⭐ CRÍTICO: Desativa o flag de processamento
                    console.log('🔄 [Debug] setIsProcessingIntegration(false) chamado para outros erros');
                    localStorage.removeItem('auth-integration-processing');
                    localStorage.removeItem('auth-loading-active');
                    
                    // 🔧 FORÇA atualização de estado para garantir que middleware veja a mudança
                    setTimeout(() => {
                         setIsProcessingIntegration(false);
                         console.log('🔄 [Debug] Força segunda atualização setIsProcessingIntegration(false) - outros erros');
                    }, 10);
                    
                    return false;
               }

               // Sucesso - criar sessão no sistema
               await createSupabaseSession(authResult.access_token!, authResult.refresh_token);
               
               // Limpa timeout de segurança ANTES de qualquer operação async
               if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
               }
               
               console.log('✅ [Integration] Sessão criada, aguardando processamento do UserContext');
               
               // Aguarda o UserContext processar a sessão e navegar automaticamente
               // NÃO removemos 'auth-integration-processing' ainda para manter o middleware ativo
               await new Promise(resolve => setTimeout(resolve, 500));
               
               // Agora força o refresh para garantir que os dados do usuário sejam carregados
               console.log('🎯 [Integration] Forçando processamento do UserContext');
               await forceRefreshAuth();
               
               // Aguarda mais um pouco para o UserContext processar
               await new Promise(resolve => setTimeout(resolve, 300));
               
               // Remove sinalização apenas DEPOIS que tudo foi processado
               localStorage.removeItem('auth-integration-processing');
               localStorage.removeItem('auth-loading-active');
               
               // ⭐ CRÍTICO: Desativa o flag de processamento apenas APÓS navegação
               setIsProcessingIntegration(false);
               console.log('✅ [Integration] setIsProcessingIntegration(false) chamado após processamento completo');
               
               // Desativa explicitamente o loading
               setLoadingState('auth-loading', false);
               console.log('✅ [Integration] setLoadingState(auth-loading, false) chamado após processamento completo');
               
               // FORÇA limpeza de TODOS os loadings globais
               ['page-loading', 'app-loading', 'route-transition'].forEach(key => {
                    setLoadingState(key, false);
               });
               console.log('🧹 [Integration] Todos os loading states globais limpos');
               
               showSuccess(`Bem-vindo(a), ${authResult.user?.nome}!`);
               
               // Navegação de fallback - só executa se o UserContext não navegou
               setTimeout(() => {
                    const currentPath = window.location.pathname;
                    if (currentPath === '/login' || currentPath.includes('/auth')) {
                         console.log('🏠 [Integration] Fallback: Redirecionando para página inicial');
                         navigate('/', { replace: true });
                    }
               }, 200);
               
               // Limpa o registro de chamada recente após sucesso
               const recentCallKey = `auth_recent_call_${anoLetivo}_${matricula}`;
               localStorage.removeItem(recentCallKey);
               
               return true;

          } catch (error: any) {
               console.error('Erro na autenticação via integração:', error);
               
               // Limpa timeout de segurança
               if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
               }
               
               const errorMessage = error.message || 'Erro interno na autenticação';
               setErrorType('GENERIC_ERROR');
               setIntegrationError(errorMessage);
               
               // Desativa loading em caso de erro
               setLoadingState('auth-loading', false);
               
               return false;
          } finally {
               // Limpa timeout de segurança
               if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
               }
               
               // Remove sinalização de processamento de integração (já removido antes se sucesso)
               localStorage.removeItem('auth-integration-processing');
               localStorage.removeItem('auth-loading-active');
               
               // Garante que loading seja desativado
               setLoadingState('auth-loading', false);
               
               // ⭐ GARANTE que o processamento seja desativado sempre
               setIsProcessingIntegration(false);
               // console.log('🔄 [Integration] setIsProcessingIntegration(false) chamado no finally'); // Reduzir logs
               
               // Reseta os controles de processamento
               apiCallRef.current = false;
               processingRef.current = false;
          }
     }, [navigate, showSuccess, setLoadingState]); // Removido dependências do UserContext e alertas não utilizados

     return {
          authenticateFromExternalSystem,
          isProcessingIntegration,
          integrationError,
          errorType,
          responsavelData,
          clearIntegrationError,
          resetProcessingState,
     };
};
